import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { notificationApi } from '@/api';
import { paths } from '@/routes/paths';
import { cn } from '@/utils/cn';
import { relativeTime } from '@/utils/format';

/** How often the badge re-checks. Often enough to feel live, rare enough to ignore. */
// The socket invalidates this the moment anything arrives, so the poll is a
// safety net for a dropped connection rather than the delivery mechanism.
const POLL_MS = 120_000;

const PREVIEW_COUNT = 6;

/**
 * The header bell: an unread badge, and a dropdown of the most recent few.
 *
 * Shared by all three signed-in shells — a notification is addressed to a
 * person, not to a role, so there is nothing role-specific here.
 */
export function NotificationBell() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const container = useRef(null);

  const { data: counts } = useQuery({
    queryKey: ['notification-count'],
    queryFn: notificationApi.getUnreadCount,
    refetchInterval: POLL_MS,
  });

  const { data: recent } = useQuery({
    queryKey: ['notifications', 'preview'],
    queryFn: () => notificationApi.listNotifications({ page: 1, page_size: PREVIEW_COUNT }),
    // Only worth fetching while the panel is on screen.
    enabled: open,
  });

  useEffect(() => {
    if (!open) return undefined;
    function onPointerDown(event) {
      if (!container.current?.contains(event.target)) setOpen(false);
    }
    function onKeyDown(event) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const read = useMutation({
    mutationFn: (id) => notificationApi.markRead(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notification-count'] });
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const readAll = useMutation({
    mutationFn: notificationApi.markAllRead,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notification-count'] });
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const unread = counts?.total ?? 0;
  const rows = recent?.data ?? [];

  function openNotification(notification) {
    if (notification.read_at === null) read.mutate(notification.id);
    setOpen(false);
    if (notification.link) navigate(notification.link);
  }

  return (
    <div className="relative" ref={container}>
      <button
        type="button"
        aria-label={unread === 0 ? 'Notifications' : `Notifications, ${unread} unread`}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((current) => !current)}
        className="text-ink-muted hover:bg-surface-muted hover:text-ink rounded-input relative flex size-9 items-center justify-center transition"
      >
        <BellIcon />
        {unread > 0 && (
          <span className="bg-brand-600 absolute top-1 right-1 flex min-w-4 items-center justify-center rounded-full px-1 text-[10px] leading-4 font-medium text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          className="border-line bg-surface rounded-card absolute right-0 z-30 mt-2 w-80 border shadow-lg"
        >
          <div className="border-line flex items-center justify-between border-b px-4 py-2.5">
            <p className="text-ink text-sm font-medium">Notifications</p>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => readAll.mutate()}
                className="text-ink-muted hover:text-ink text-xs hover:underline"
              >
                Mark all read
              </button>
            )}
          </div>

          {rows.length === 0 ? (
            <p className="text-ink-muted px-4 py-6 text-center text-sm">Nothing yet.</p>
          ) : (
            <ul className="max-h-96 overflow-y-auto">
              {rows.map((notification) => (
                <li key={notification.id}>
                  <button
                    type="button"
                    onClick={() => openNotification(notification)}
                    className={cn(
                      'hover:bg-surface-muted block w-full px-4 py-3 text-left transition',
                      notification.read_at === null && 'bg-brand-50/40',
                    )}
                  >
                    <p className="text-ink text-sm font-medium">{notification.title}</p>
                    <p className="text-ink-muted mt-0.5 line-clamp-2 text-xs whitespace-pre-line wrap-anywhere">
                      {notification.body}
                    </p>
                    <p className="text-ink-muted mt-1 text-[11px]">
                      {relativeTime(notification.created_at)}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="border-line border-t px-4 py-2.5 text-center">
            <Link
              to={paths.notifications}
              onClick={() => setOpen(false)}
              className="text-brand-700 text-xs hover:underline"
            >
              See all
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function BellIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="size-5"
      aria-hidden="true"
    >
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.7 21a2 2 0 0 1-3.4 0" />
    </svg>
  );
}
